<template>
  <div class="step-card">

    <!-- Header -->
    <div class="step-header">
      <div class="step-icon">
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <rect
            x="3"
            y="5"
            width="18"
            height="14"
            rx="3"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
          />

          <path
            d="M4 7L12 13L20 7"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <div class="step-heading">
        <span class="step-label">
          Контактная информация
        </span>

        <h3>
          Рабочая почта компании
        </h3>
      </div>
    </div>

    <p class="description">
      Укажите актуальную рабочую почту. На неё будут приходить
      обращения от кандидатов и важные уведомления, связанные
      с компанией.
    </p>

    <div class="form-section">
      <div class="field-heading">
        <label
          class="field-label"
          for="company-email"
        >
          Email компании
        </label>

        <span class="field-badge">
          Рабочий email
        </span>
      </div>

      <div
        class="input-wrapper"
        :class="{
          invalid:
            isTouched &&
            !isValid,

          valid:
            isTouched &&
            isValid
        }"
      >
        <div class="input-icon">
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <rect
              x="3"
              y="5"
              width="18"
              height="14"
              rx="3"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
            />

            <path
              d="M4 7L12 13L20 7"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <input
          id="company-email"
          v-model="model.email"
          type="email"
          class="text-input"
          placeholder="Например: hr@company.com"
          maxlength="120"
          autocomplete="email"
          inputmode="email"
          autocapitalize="none"
          spellcheck="false"
          @input="isTouched = true"
          @blur="normalizeEmail"
        />

        <transition name="status">
          <div
            v-if="
              isTouched &&
              isValid
            "
            class="valid-status"
          >
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M5 12.5L10 17L19 7"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </transition>
      </div>

      <transition
        name="message"
        mode="out-in"
      >
        <p
          v-if="
            isTouched &&
            !isValid
          "
          key="error"
          class="field-message error"
        >
          <span class="message-icon">
            !
          </span>

          Проверьте формат почты, например hr@company.com
        </p>

        <p
          v-else
          key="hint"
          class="field-message hint"
        >
          Используйте адрес, к которому у команды есть
          постоянный доступ.
        </p>
      </transition>

      <div class="security-note">
        <div class="security-icon">
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M8 11V8C8 5.8 9.8 4 12 4C14.2 4 16 5.8 16 8V11"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />

            <rect
              x="5"
              y="10"
              width="14"
              height="10"
              rx="3"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
            />
          </svg>
        </div>

        <div class="security-text">
          <strong>
            Защищённый контакт
          </strong>

          <span>
            Мы используем email для связи по вопросам компании
            и обращений кандидатов.
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  ref,
  watch
} from "vue";

const model = defineModel({
  required: true
});

const emit = defineEmits([
  "valid",
  "not-valid"
]);

if (
  typeof model.value.email !==
  "string"
) {
  model.value.email = "";
}

const isTouched = ref(
  model.value.email.trim().length > 0
);

const normalizedEmail = computed(() => {
  return model.value.email
    .trim()
    .toLowerCase();
});

const isValid = computed(() => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    .test(normalizedEmail.value);
});

function normalizeEmail() {
  isTouched.value = true;

  model.value.email =
    normalizedEmail.value;
}

watch(
  () => model.value.email,

  () => {
    if (isValid.value) {
      emit("valid");
    } else {
      emit("not-valid");
    }
  },

  {
    immediate: true
  }
);
</script>

<style scoped>
.step-card {
  width: 100%;

  padding:
    26px
    24px;

  box-sizing: border-box;

  background: #ffffff;
  border-radius: 28px;

  animation:
    step-appear
    0.42s
    cubic-bezier(0.22, 1, 0.36, 1);
}

/* Header */

.step-header {
  max-width: 620px;

  display: flex;
  align-items: center;
  gap: 13px;

  margin-bottom: 18px;
}

.step-icon {
  width: 46px;
  height: 46px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #287ff0;

  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.13),
      rgba(44, 173, 230, 0.08)
    );

  border: 1px solid rgba(40, 127, 240, 0.12);
  border-radius: 14px;

  animation:
    icon-appear
    0.5s
    cubic-bezier(0.22, 1, 0.36, 1);
}

.step-icon svg {
  width: 25px;
  height: 25px;
}

.step-heading {
  min-width: 0;

  display: flex;
  flex-direction: column;
  gap: 3px;
}

.step-label {
  color: #969ba5;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.step-heading h3 {
  margin: 0;

  color: #24272d;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 19px;
  line-height: 1.3;
}

.description {
  max-width: 620px;

  margin:
    0
    0
    23px;

  color: #777c86;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.55;

  animation:
    content-appear
    0.4s
    ease
    0.06s
    both;
}

/* Form */

.form-section {
  width: 100%;
  max-width: 540px;

  animation:
    content-appear
    0.42s
    ease
    0.12s
    both;
}

.field-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;

  margin-bottom: 8px;
}

.field-label {
  color: #40444c;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
}

.field-badge {
  padding:
    5px
    8px;

  color: #3480dc;
  background: rgba(40, 127, 240, 0.08);

  border: 1px solid rgba(40, 127, 240, 0.12);
  border-radius: 999px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;
}

/* Input */

.input-wrapper {
  width: 100%;
  height: 52px;

  display: flex;
  align-items: center;

  box-sizing: border-box;

  background: #f5f6f8;
  border: 1px solid #dfe2e7;
  border-radius: 14px;

  box-shadow:
    inset 0 1px 2px
    rgba(24, 34, 55, 0.035);

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.input-wrapper:focus-within {
  background: #ffffff;
  border-color: #5b8dfd;

  box-shadow:
    0 0 0 3px
    rgba(91, 141, 253, 0.14);

  transform: translateY(-1px);
}

.input-wrapper.invalid {
  border-color: #e36a72;

  box-shadow:
    0 0 0 3px
    rgba(222, 75, 87, 0.09);

  animation:
    input-shake
    0.32s
    ease;
}

.input-wrapper.valid {
  border-color: rgba(52, 170, 111, 0.5);
}

.input-icon {
  width: 48px;
  height: 100%;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #8c929d;

  transition:
    color 0.2s ease,
    transform 0.2s ease;
}

.input-icon svg {
  width: 20px;
  height: 20px;
}

.input-wrapper:focus-within
.input-icon {
  color: #4b83ec;

  transform: translateY(-1px);
}

.text-input {
  min-width: 0;
  height: 100%;
  flex: 1;

  padding: 0 8px 0 0;

  box-sizing: border-box;

  color: #202329;
  background: transparent;

  border: none;
  outline: none;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
}

.text-input::placeholder {
  color: #a6aab2;
}

/* Valid status */

.valid-status {
  width: 29px;
  height: 29px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-right: 11px;

  color: #24965c;
  background: rgba(42, 174, 105, 0.1);
  border-radius: 9px;
}

.valid-status svg {
  width: 16px;
  height: 16px;
}

/* Messages */

.field-message {
  min-height: 20px;

  display: flex;
  align-items: flex-start;
  gap: 7px;

  margin:
    8px
    2px
    0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  line-height: 1.45;
}

.hint {
  color: #9297a1;
}

.error {
  color: #ce4d58;
}

.message-icon {
  width: 17px;
  height: 17px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-top: -1px;

  color: #ffffff;
  background: #db5964;
  border-radius: 50%;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 10px;
}

/* Security */

.security-note {
  display: flex;
  align-items: flex-start;
  gap: 11px;

  margin-top: 22px;

  padding:
    14px
    15px;

  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.055),
      rgba(44, 173, 230, 0.035)
    );

  border: 1px solid rgba(40, 127, 240, 0.1);
  border-radius: 15px;
}

.security-icon {
  width: 34px;
  height: 34px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #3c82db;
  background: rgba(40, 127, 240, 0.09);
  border-radius: 10px;
}

.security-icon svg {
  width: 19px;
  height: 19px;
}

.security-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.security-text strong {
  color: #41454d;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;
  font-weight: normal;
}

.security-text span {
  color: #8b9099;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  line-height: 1.45;
}

/* Transitions */

.status-enter-active,
.status-leave-active,
.message-enter-active,
.message-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.status-enter-from,
.status-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.message-enter-from,
.message-leave-to {
  opacity: 0;
  transform: translateY(-3px);
}

/* Dark theme */

:global(html[data-theme="dark"]) .step-card {
  background: #1c1c1e;
}

:global(html[data-theme="dark"])
.step-heading h3 {
  color: #f0f1f3;
}

:global(html[data-theme="dark"])
.step-label {
  color: #81858e;
}

:global(html[data-theme="dark"])
.description {
  color: #a7aab1;
}

:global(html[data-theme="dark"])
.field-label {
  color: #d4d6da;
}

:global(html[data-theme="dark"])
.field-badge {
  color: #73aafa;

  background: rgba(59, 130, 246, 0.11);
  border-color: rgba(59, 130, 246, 0.16);
}

:global(html[data-theme="dark"])
.input-wrapper {
  background: #292a2e;
  border-color: #414349;

  box-shadow:
    inset 0 1px 2px
    rgba(0, 0, 0, 0.2);
}

:global(html[data-theme="dark"])
.input-wrapper:focus-within {
  background: #2d2f34;
  border-color: #5989eb;

  box-shadow:
    0 0 0 3px
    rgba(70, 126, 229, 0.15);
}

:global(html[data-theme="dark"])
.text-input {
  color: #ffffff;
}

:global(html[data-theme="dark"])
.text-input::placeholder {
  color: #70747c;
}

:global(html[data-theme="dark"])
.input-icon {
  color: #7f848e;
}

:global(html[data-theme="dark"])
.hint {
  color: #7f838b;
}

:global(html[data-theme="dark"])
.security-note {
  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.09),
      rgba(44, 173, 230, 0.05)
    );

  border-color: rgba(59, 130, 246, 0.14);
}

:global(html[data-theme="dark"])
.security-text strong {
  color: #d6d8dc;
}

:global(html[data-theme="dark"])
.security-text span {
  color: #858992;
}

/* Animations */

@keyframes step-appear {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes content-appear {
  from {
    opacity: 0;
    transform: translateY(7px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes icon-appear {
  from {
    opacity: 0;
    transform: scale(0.75) rotate(-8deg);
  }

  to {
    opacity: 1;
    transform: scale(1) rotate(0);
  }
}

@keyframes input-shake {
  0%,
  100% {
    transform: translateX(0);
  }

  30% {
    transform: translateX(-3px);
  }

  60% {
    transform: translateX(3px);
  }
}

/* Mobile */

@media (max-width: 640px) {
  .step-card {
    padding:
      22px
      16px;
  }

  .step-heading h3 {
    font-size: 18px;
  }

  .form-section {
    max-width: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .step-card,
  .step-icon,
  .description,
  .form-section,
  .input-wrapper {
    animation: none;
  }

  .input-wrapper,
  .input-icon,
  .status-enter-active,
  .status-leave-active,
  .message-enter-active,
  .message-leave-active {
    transition: none;
  }
}

html[data-theme="dark"] .step-card {
  background-color: #1c1c1e;
}

html[data-theme="dark"] .step-icon {
  color: #69a7ff;

  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.18),
      rgba(44, 173, 230, 0.11)
    );

  border-color: rgba(80, 150, 255, 0.2);
}

html[data-theme="dark"] .step-heading h3 {
  color: #f2f2f4;
}

html[data-theme="dark"] .step-label {
  color: #81858e;
}

html[data-theme="dark"] .description {
  color: #a7aab1;
}

html[data-theme="dark"] .field-label {
  color: #d4d6da;
}

html[data-theme="dark"] .field-badge {
  color: #78afff;

  background-color: rgba(59, 130, 246, 0.12);
  border-color: rgba(59, 130, 246, 0.18);
}

html[data-theme="dark"] .input-wrapper {
  background-color: #292a2e;
  border-color: #414349;

  box-shadow:
    inset 0 1px 2px
    rgba(0, 0, 0, 0.2);
}

html[data-theme="dark"] .input-wrapper:focus-within {
  background-color: #2d2f34;
  border-color: #5989eb;

  box-shadow:
    0 0 0 3px
    rgba(70, 126, 229, 0.15),
    inset 0 1px 2px
    rgba(0, 0, 0, 0.18);
}

html[data-theme="dark"] .input-wrapper.invalid {
  border-color: #e46d76;

  box-shadow:
    0 0 0 3px
    rgba(228, 77, 89, 0.13);
}

html[data-theme="dark"] .input-wrapper.valid {
  border-color: rgba(61, 192, 126, 0.62);
}

html[data-theme="dark"] .input-icon {
  color: #7f848e;
}

html[data-theme="dark"] .input-wrapper:focus-within .input-icon {
  color: #67a2ff;
}

html[data-theme="dark"] .text-input {
  color: #ffffff;
}

html[data-theme="dark"] .text-input::placeholder {
  color: #70747c;
}

html[data-theme="dark"] .valid-status {
  color: #51c989;
  background-color: rgba(61, 192, 126, 0.14);
}

html[data-theme="dark"] .hint {
  color: #7f838b;
}

html[data-theme="dark"] .error {
  color: #ff8b94;
}

html[data-theme="dark"] .message-icon {
  color: #ffffff;
  background-color: #df5b67;
}

html[data-theme="dark"] .security-note {
  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.11),
      rgba(44, 173, 230, 0.07)
    );

  border-color: rgba(59, 130, 246, 0.18);
}

html[data-theme="dark"] .security-icon {
  color: #73aaff;
  background-color: rgba(59, 130, 246, 0.14);
}

html[data-theme="dark"] .security-text strong {
  color: #d9dadd;
}

html[data-theme="dark"] .security-text span {
  color: #858992;
}

</style>